import React, { useState, useEffect } from "react";
import {
  Stack,
  Card,
  Shelf,
  Text,
  Icon,
  Progress,
  Button,
  Token,
} from "@chaser/components";
import useOrganisationUtils from "../../../../../hooks/useOrganisationUtils";
import LockIconBadge from "../../../../../components/LockIconBadge";
import {
  formatAddonFrom,
  formatRecurringAmount,
  getRowActionLabel,
  toMinor,
} from "../utils";

import styles from "../Addons.module.scss";

const joinClasses = (...classNames) => classNames.filter(Boolean).join(" ");

const isScheduledForRemoval = (row) =>
  Boolean(row?.usage?.scheduled) &&
  Number(row?.usage?.scheduled?.overages || 0) === 0 &&
  Number(row?.usage?.scheduled?.overagesChange || 0) < 0;

const AddonRowsSection = ({
  title,
  rows,
  sectionType,
  billingTerm,
  currencyCode,
  onActionClick,
  onRemoveClick,
  removingAddonId = null,
  pendingAddonId = null,
  onCancelScheduledChange,
  isLoadingCancelScheduled = false,
}) => {
  const [cancellingAddonId, setCancellingAddonId] = useState(null);
  const { formatCurrency } = useOrganisationUtils();
  const isCredits = sectionType === "credits";
  const usageTermLabel = billingTerm === "year" ? "annual period" : "month";
  const recurringSuffix = billingTerm === "year" ? " /yr" : " /mo";
  const handleCardAction = (row) => onActionClick(row);
  const formatRecurring = (amountMinor) =>
    formatRecurringAmount(
      amountMinor,
      formatCurrency,
      currencyCode,
      recurringSuffix,
    );
  const formatFrom = (row) =>
    formatAddonFrom(row, formatCurrency, currencyCode);

  useEffect(() => {
    if (cancellingAddonId && !isLoadingCancelScheduled) {
      const cancelledRow = rows.find(
        (row) => row.addonId === cancellingAddonId,
      );
      if (cancelledRow) {
        const isStillScheduled = isScheduledForRemoval(cancelledRow);

        if (!isStillScheduled) {
          setCancellingAddonId(null);
        }
      }
    }
  }, [cancellingAddonId, isLoadingCancelScheduled, rows]);

  return (
    <Stack gap="small">
      <Shelf alignItems="center" gap="small" className={styles.sectionHeading}>
        <Text fontSize="12px" className={styles.sectionTitleText}>
          {title}
        </Text>
        <div className={styles.sectionTitleLine} />
      </Shelf>
      {rows.map((row) =>
        (() => {
          const isUnlimitedPlanLimit =
            Number(row?.includedLimit) >= 9999 ||
            Number(row?.limit) >= 9999 ||
            Number(row?.usage?.planLimit) >= 9999;
          const actionLabel = getRowActionLabel(row);
          const disableIncludedFlatFeeAction = row.isIncluded && row.isFlatFee;
          const showIncludedToken = row.isIncluded;
          const showLockedIcon =
            !isUnlimitedPlanLimit && !row.isIncluded && !row.isActive;
          const isOverLimit = Boolean(row.isOverLimit);
          const usageProgressNow = isOverLimit
            ? 100
            : Math.min(100, row.percentage || 0);
          const showPerUnitFeatureUsage =
            !isCredits && row.addon.type === "PER_UNIT" && row.limit > 0;
          const hasReachedLimit =
            !isOverLimit && row.limit > 0 && row.used >= row.limit;
          const isLimitState = isOverLimit || hasReachedLimit;
          const showVolumeOverLimitNotice =
            row.addon.type === "VOLUME" && isOverLimit;
          const usageStatusLabel = isOverLimit
            ? "Over limit"
            : hasReachedLimit
              ? "Reached limit"
              : `${row.percentage}%`;
          const usageSummaryLabel =
            row.addon.type === "PER_UNIT"
              ? "used"
              : `used this ${usageTermLabel}`;
          const buttonClassName = !row.isActive
            ? styles.primaryCta
            : actionLabel === "Remove"
              ? styles.removeCta
              : "";
          const purchasedQuantity = Number(
            row?.purchasedQuantity ?? row?.purchasedAllowance ?? 0,
          );
          const purchasedAmountMinor = Number(row?.rowAmountMinor || 0);
          const showSecondaryRemoveAction =
            row?.isActive &&
            actionLabel !== "Remove" &&
            (purchasedQuantity > 0 || purchasedAmountMinor > 0);
          const scheduledForRemoval = isScheduledForRemoval(row);
          const isPendingAddonAction = pendingAddonId === row.addonId;
          const showUsageCountOnly = isUnlimitedPlanLimit;
          const isActionBlocked =
            disableIncludedFlatFeeAction ||
            scheduledForRemoval ||
            isPendingAddonAction ||
            isUnlimitedPlanLimit;
          const rowClassName = joinClasses(
            styles.addonRow,
            !scheduledForRemoval &&
              !isPendingAddonAction &&
              styles.addonRowInteractive,
            !row.isActive && styles.notSubscribed,
            !isCredits && styles.featureAddonRow,
            showPerUnitFeatureUsage && styles.featureAddonRowWithUsage,
            isOverLimit && styles.overLimitRow,
            cancellingAddonId === row.addonId &&
              (isLoadingCancelScheduled || scheduledForRemoval) &&
              styles.loading,
            (removingAddonId === row.addonId || isPendingAddonAction) &&
              styles.loading,
          );

          return (
            <Card
              key={row.addonId}
              p="small"
              className={rowClassName}
              onClick={
                isActionBlocked ? undefined : () => handleCardAction(row)
              }
              onKeyDown={(event) => {
                if (isActionBlocked) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleCardAction(row);
                }
              }}
              role={isActionBlocked ? undefined : "button"}
              tabIndex={isActionBlocked ? -1 : 0}
            >
              <Shelf
                justifyContent="space-between"
                alignItems="stretch"
                gap="medium"
                className={styles.rowContent}
              >
                <Stack className={styles.rowLeft} gap="small">
                  <Shelf alignItems="center" justifyContent="space-between">
                    <Shelf alignItems="center" gap="xsmall">
                      <Icon name={row.icon} size={14} color="Mid Neutral 1" />
                      <Text fontWeight="bold">{row.name}</Text>
                    </Shelf>
                    {showIncludedToken && (
                      <Token
                        size="narrow"
                        className={styles.statusTokenIncluded}
                      >
                        Included with your plan
                      </Token>
                    )}
                    {showLockedIcon && <LockIconBadge />}
                  </Shelf>

                  {(Number(row?.includedLimit) > 0 || row?.isIncluded) && (
                    <Text
                      color="Mid Neutral 1"
                      className={styles.includedPlanCopy}
                    >
                      {Number(row?.includedLimit) > 0
                        ? `${isUnlimitedPlanLimit ? "Unlimited" : row.includedLimit} included with plan`
                        : "Included with plan"}
                    </Text>
                  )}

                  {isCredits && row.limit > 0 && (
                    <Stack gap="xsmall" className={styles.usageMeterWrap}>
                      {showUsageCountOnly ? (
                        <Text color="Mid Neutral 1">
                          <strong className={styles.usageValue}>
                            {row.used}
                          </strong>{" "}
                          used
                        </Text>
                      ) : (
                        <>
                          <Shelf
                            justifyContent="space-between"
                            className={styles.usageRow}
                          >
                            <Text
                              color={isLimitState ? "Red" : "Mid Neutral 1"}
                              className={
                                isLimitState ? styles.overLimitUsageText : ""
                              }
                            >
                              <strong
                                className={`${styles.usageValue} ${isLimitState ? styles.overLimitUsageValue : ""}`}
                              >
                                {row.used}
                              </strong>{" "}
                              of{" "}
                              <strong
                                className={`${styles.usageValue} ${isLimitState ? styles.overLimitUsageValue : ""}`}
                              >
                                {row.limit}
                              </strong>{" "}
                              {usageSummaryLabel}
                            </Text>
                            <Text
                              color={isLimitState ? "Red" : "Mid Neutral 1"}
                            >
                              {usageStatusLabel}
                            </Text>
                          </Shelf>
                          <div className={styles.usageBarWrap}>
                            <Progress
                              now={usageProgressNow}
                              min={0}
                              max={100}
                              height="8px"
                              color={isLimitState ? "Red" : "Orange"}
                              className={styles.usageBar}
                            />
                          </div>
                        </>
                      )}
                      {showVolumeOverLimitNotice && (
                        <Shelf alignItems="center" gap="xsmall">
                          <Icon
                            name="warning-circle"
                            size={14}
                            color="Mid Neutral 1"
                          />
                          <Text color="Mid Neutral 1" fontSize="small">
                            You have gone beyond the allowance and will be
                            charged overage
                          </Text>
                        </Shelf>
                      )}
                    </Stack>
                  )}

                  {showPerUnitFeatureUsage && (
                    <Stack gap="xsmall" className={styles.usageMeterWrap}>
                      {showUsageCountOnly ? (
                        <Text color="Mid Neutral 1">
                          <strong className={styles.usageValue}>
                            {row.used}
                          </strong>{" "}
                          used
                        </Text>
                      ) : (
                        <>
                          <Shelf
                            justifyContent="space-between"
                            className={styles.usageRow}
                          >
                            <Text
                              color={isLimitState ? "Red" : "Mid Neutral 1"}
                              className={
                                isLimitState ? styles.overLimitUsageText : ""
                              }
                            >
                              <strong
                                className={`${styles.usageValue} ${isLimitState ? styles.overLimitUsageValue : ""}`}
                              >
                                {row.used}
                              </strong>{" "}
                              of{" "}
                              <strong
                                className={`${styles.usageValue} ${isLimitState ? styles.overLimitUsageValue : ""}`}
                              >
                                {row.limit}
                              </strong>{" "}
                              {usageSummaryLabel}
                            </Text>
                            <Text
                              color={isLimitState ? "Red" : "Mid Neutral 1"}
                            >
                              {usageStatusLabel}
                            </Text>
                          </Shelf>
                          <div className={styles.usageBarWrap}>
                            <Progress
                              now={usageProgressNow}
                              min={0}
                              max={100}
                              height="8px"
                              color={isLimitState ? "Red" : "Orange"}
                              className={styles.usageBar}
                            />
                          </div>
                        </>
                      )}
                    </Stack>
                  )}

                  {scheduledForRemoval && onCancelScheduledChange && (
                    <Stack
                      gap="small"
                      className={styles.scheduledRemovalNotice}
                      py="xxsmall"
                      px="small"
                    >
                      <Shelf
                        gap="small"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Text color="Red" fontSize="small" fontWeight="bold">
                          × Scheduled for removal
                        </Text>
                        <Button
                          appearance="minimal"
                          onClick={(event) => {
                            event.stopPropagation();
                            setCancellingAddonId(row.addonId);
                            onCancelScheduledChange(row);
                          }}
                          disabled={
                            cancellingAddonId === row.addonId &&
                            isLoadingCancelScheduled
                          }
                          data-trackingid={`addon-row-cancel-removal-${row.addonId}`}
                        >
                          Cancel
                        </Button>
                      </Shelf>
                    </Stack>
                  )}
                </Stack>

                {!isUnlimitedPlanLimit && (
                  <Stack
                    className={styles.rowRight}
                    alignItems="flex-end"
                    gap="small"
                  >
                    <Text
                      fontWeight="bold"
                      className={`${styles.rowAmount} ${row.isIncluded ? styles.rowAmountIncluded : ""}`}
                    >
                      {row.isActive
                        ? formatRecurring(row.rowAmountMinor)
                        : formatFrom(row)}
                    </Text>

                    {isCredits && row.addon.type !== "FLAT_FEE" && (
                      <Text color="Mid Neutral 1" className={styles.rowMeta}>
                        {formatCurrency(
                          toMinor(
                            row.usage?.price ||
                              row.addon.price ||
                              row.addon?.tiers?.[0]?.price,
                          ) / 100,
                          currencyCode,
                        )}{" "}
                        per credit
                      </Text>
                    )}

                    {!isCredits && !row.isIncluded && (
                      <Text color="Mid Neutral 1" className={styles.rowMeta}>
                        {row.addon.type === "FLAT_FEE" ? "Flat rate" : ""}
                      </Text>
                    )}

                    <Stack gap="xsmall">
                      {!disableIncludedFlatFeeAction &&
                        !scheduledForRemoval && (
                          <Button
                            appearance="secondary"
                            size="narrow"
                            className={joinClasses(
                              styles.actionButtonBase,
                              buttonClassName,
                            )}
                            disabled={isPendingAddonAction}
                            onClick={(event) => {
                              event.stopPropagation();
                              onActionClick(row);
                            }}
                          >
                            {actionLabel}
                          </Button>
                        )}

                      {showSecondaryRemoveAction &&
                        onRemoveClick &&
                        !scheduledForRemoval && (
                          <Button
                            appearance="secondary"
                            size="narrow"
                            className={joinClasses(
                              styles.actionButtonBase,
                              styles.removeCta,
                            )}
                            disabled={isPendingAddonAction}
                            onClick={(event) => {
                              event.stopPropagation();
                              onRemoveClick(row);
                            }}
                          >
                            Remove
                          </Button>
                        )}
                    </Stack>
                  </Stack>
                )}
              </Shelf>
            </Card>
          );
        })(),
      )}
    </Stack>
  );
};

export default AddonRowsSection;
